import { Link } from "react-router-dom";

interface ListItem {
  id: number;
  title: string;
  category: string;
  src: string;
  alt: string;
  href?: string;
}

interface RollingTextItemProps {
  item: ListItem;
}

function RollingTextItem({ item }: RollingTextItemProps) {
  const content = (
    <div className="relative flex items-center justify-between py-3 md:py-4">
      {/* Rolling text */}
      <div className="relative overflow-hidden h-[3.75rem] md:h-[5.5rem]">
        <div className="transition-transform duration-500 ease-in-out group-hover/item:-translate-y-1/2">
          {/* State 1: Normal */}
          <div className="h-[3.75rem] md:h-[5.5rem] flex items-center">
            <span className="text-5xl md:text-7xl font-bold tracking-tight text-foreground transition-all duration-500 whitespace-nowrap">
              {item.title}
            </span>
          </div>

          {/* State 2: Hover (Italic + Color) */}
          <div className="h-[3.75rem] md:h-[5.5rem] flex items-center">
            <span className="text-5xl md:text-7xl font-bold tracking-tight italic text-primary transition-all duration-500 whitespace-nowrap">
              {item.title}
            </span>
          </div>
        </div>
      </div>

      {/* Category Label */}
      <span className="text-sm md:text-base text-muted-foreground font-medium uppercase tracking-wider">
        {item.category}
      </span>

      {/* Image Reveal Effect */}
      <div className="absolute right-24 md:right-32 top-1/2 -translate-y-1/2 pointer-events-none opacity-0 group-hover/item:opacity-100 transition-opacity duration-300 z-10">
        <div className="w-24 h-16 md:w-32 md:h-20 rounded-lg overflow-hidden shadow-lg">
          <img
            src={item.src}
            alt={item.alt}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  );

  if (item.href) {
    return (
      <Link to={item.href} className="group/item block border-b border-border cursor-pointer hover:bg-muted/50 transition-colors">
        {content}
      </Link>
    );
  }

  return (
    <div className="group/item border-b border-border cursor-pointer">
      {content}
    </div>
  );
}

interface RollingTextListProps {
  items: ListItem[];
  title?: string;
  subtitle?: string;
}

function RollingTextList({ items, title, subtitle }: RollingTextListProps) {
  return (
    <div className="w-full max-w-4xl mx-auto">
      {title && (
        <div className="mb-6 md:mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground">{title}</h2>
          {subtitle && <p className="text-muted-foreground mt-1">{subtitle}</p>}
        </div>
      )}

      <div className="border-t border-border">
        {items.map((item) => (
          <RollingTextItem key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
}

export { RollingTextList };
export type { ListItem, RollingTextListProps };
